import { CloseIcon } from '@chakra-ui/icons';
import {
  Box,
  FormControl,
  FormLabel,
  HStack,
  IconButton,
  Image,
  Input,
  SimpleGrid,
  Spinner,
  Textarea,
  VStack,
} from '@chakra-ui/react';
import { useUploadAssetMutation } from 'hooks/mutations/useUploadAssetMutation';
import { useRef } from 'react';

import DevSandboxTooltip from 'components/DevSandbox/DevSandboxTooltip';
import Icon from 'components/Icon';
import Text from 'components/Text';

import { CVPersonalInfo } from '../types';
import { getDisplayPhotoUrl } from '../utils/cvDefaults';

interface PersonalInfoFormProps {
  personalInfo: CVPersonalInfo;
  onChange: (field: keyof CVPersonalInfo, value: string | boolean) => void;
  cvId?: string;
  showPhoto: boolean;
}

const PersonalInfoForm = ({ personalInfo, onChange, cvId, showPhoto }: PersonalInfoFormProps) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const uploadMutation = useUploadAssetMutation();
  const displayPhotoUrl = getDisplayPhotoUrl(personalInfo.photoUrl, personalInfo.fullName);

  return (
    <VStack spacing={4} alignItems="stretch">
      {/* Photo + Header */}
      <VStack spacing={2} alignItems="flex-start">
        {showPhoto && (
          displayPhotoUrl ? (
            <Box position="relative" flexShrink={0}>
              <Image
                src={displayPhotoUrl}
                alt="Photo"
                boxSize="56px"
                borderRadius="full"
                objectFit="cover"
                border="2px solid"
                borderColor="whiteAlpha.200"
                fallback={
                  <Box
                    boxSize="56px"
                    borderRadius="full"
                    backgroundColor="whiteAlpha.100"
                    border="2px solid"
                    borderColor="whiteAlpha.200"
                  />
                }
              />
              {personalInfo.photoUrl && (
                <IconButton
                  aria-label="Remove photo"
                  icon={<CloseIcon boxSize={1.5} />}
                  size="xs"
                  variant="solid"
                  colorScheme="red"
                  borderRadius="full"
                  position="absolute"
                  top={-1}
                  right={-1}
                  w="16px"
                  h="16px"
                  minW="16px"
                  onClick={() => onChange('photoUrl', '')}
                />
              )}
            </Box>
          ) : (
            <Box
              boxSize="56px"
              borderRadius="full"
              backgroundColor="whiteAlpha.100"
              border="2px dashed"
              borderColor="whiteAlpha.200"
              flexShrink={0}
            />
          )
        )}
        <VStack spacing={0} alignItems="flex-start">
          <HStack spacing={1.5}>
            <Box color="purple.300" display="flex" alignItems="center">
              <svg width="16" height="16" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 6a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0zM4.501 20.118a7.5 7.5 0 0114.998 0A17.933 17.933 0 0112 21.75c-2.676 0-5.216-.584-7.499-1.632z" />
              </svg>
            </Box>
            <Input
              value={personalInfo.aboutTitle || 'About Me'}
              onChange={(e) => onChange('aboutTitle', e.target.value)}
              variant="unstyled"
              fontWeight="semibold"
              size="sm"
              placeholder="About Me"
              _focus={{ borderBottom: '1px solid', borderColor: 'purple.400' }}
            />
          </HStack>
          {showPhoto && (
            <Text fontSize="micro" opacity="tertiary">
              {personalInfo.photoUrl ? 'Custom photo' : personalInfo.fullName.trim() ? 'Auto-generated avatar' : 'No photo'}
            </Text>
          )}
        </VStack>
      </VStack>

      {/* Photo URL input + Upload — hidden when showPhoto is off */}
      {showPhoto && (
        <FormControl>
          <HStack spacing={1} mb={0}>
            <FormLabel fontSize="xs" color="whiteAlpha.600" mb={0}>
              Photo URL
            </FormLabel>
            <DevSandboxTooltip
              label="Optional. Photos on CVs are common in EU but uncommon in the US where they can lead to bias concerns."
              placement="top"
            >
              <Box
                display="flex"
                color="whiteAlpha.400"
                cursor="help"
              >
                <Icon type="Tooltip" size={14} />
              </Box>
            </DevSandboxTooltip>
          </HStack>
          <HStack spacing={2} mt={1}>
            <Input
              value={personalInfo.photoUrl}
              onChange={(e) => onChange('photoUrl', e.target.value)}
              placeholder="https://example.com/photo.jpg"
              variant="filled"
              size="sm"
              flex={1}
            />
            <input
              ref={fileInputRef}
              type="file"
              accept="image/png,image/jpeg,image/webp"
              style={{ display: 'none' }}
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file && cvId) {
                  uploadMutation.mutate(
                    { file, entityType: 'cv', entityId: cvId },
                    { onSuccess: (data) => onChange('photoUrl', data.publicUrl) }
                  );
                }
                e.target.value = '';
              }}
            />
            <Box
              as="button"
              type="button"
              px={3}
              py={1}
              borderRadius="md"
              backgroundColor="whiteAlpha.100"
              border="1px solid"
              borderColor="whiteAlpha.200"
              style={{ fontSize: '12px', whiteSpace: 'nowrap' }}
              color="whiteAlpha.700"
              cursor={cvId ? 'pointer' : 'not-allowed'}
              opacity={cvId ? 1 : 0.5}
              _hover={cvId ? { backgroundColor: 'whiteAlpha.200' } : {}}
              transition="all 0.15s"
              onClick={() => cvId && fileInputRef.current?.click()}
              disabled={uploadMutation.isPending}
            >
              {uploadMutation.isPending ? <Spinner size="xs" /> : 'Upload'}
            </Box>
          </HStack>
        </FormControl>
      )}

      <SimpleGrid columns={[1, 2]} gap={3}>
        <FormControl>
          <FormLabel fontSize="xs" color="whiteAlpha.600">
            Full Name
          </FormLabel>
          <Input
            value={personalInfo.fullName}
            onChange={(e) => onChange('fullName', e.target.value)}
            placeholder="John Doe"
            variant="filled"
            size="sm"
          />
        </FormControl>
        <FormControl>
          <FormLabel fontSize="xs" color="whiteAlpha.600">
            Job Title
          </FormLabel>
          <Input
            value={personalInfo.jobTitle}
            onChange={(e) => onChange('jobTitle', e.target.value)}
            placeholder="Senior Frontend Developer"
            variant="filled"
            size="sm"
          />
        </FormControl>
      </SimpleGrid>

      <SimpleGrid columns={[1, 2]} gap={3}>
        <FormControl>
          <FormLabel fontSize="xs" color="whiteAlpha.600">
            Email
          </FormLabel>
          <Input
            value={personalInfo.email}
            onChange={(e) => onChange('email', e.target.value)}
            placeholder="john@example.com"
            variant="filled"
            size="sm"
            type="email"
          />
        </FormControl>
        <FormControl>
          <FormLabel fontSize="xs" color="whiteAlpha.600">
            Phone
          </FormLabel>
          <Input
            value={personalInfo.phone}
            onChange={(e) => onChange('phone', e.target.value)}
            placeholder="+1 234 567 890"
            variant="filled"
            size="sm"
          />
        </FormControl>
      </SimpleGrid>

      <SimpleGrid columns={[1, 2]} gap={3}>
        <FormControl>
          <FormLabel fontSize="xs" color="whiteAlpha.600">
            Location
          </FormLabel>
          <Input
            value={personalInfo.location}
            onChange={(e) => onChange('location', e.target.value)}
            placeholder="San Francisco, CA"
            variant="filled"
            size="sm"
          />
        </FormControl>
        <FormControl>
          <FormLabel fontSize="xs" color="whiteAlpha.600">
            Website
          </FormLabel>
          <Input
            value={personalInfo.website}
            onChange={(e) => onChange('website', e.target.value)}
            placeholder="yoursite.com"
            variant="filled"
            size="sm"
          />
        </FormControl>
      </SimpleGrid>

      <SimpleGrid columns={[1, 2]} gap={3}>
        <FormControl>
          <FormLabel fontSize="xs" color="whiteAlpha.600">
            LinkedIn
          </FormLabel>
          <Input
            value={personalInfo.linkedin}
            onChange={(e) => onChange('linkedin', e.target.value)}
            placeholder="linkedin.com/in/johndoe"
            variant="filled"
            size="sm"
          />
        </FormControl>
        <FormControl>
          <FormLabel fontSize="xs" color="whiteAlpha.600">
            GitHub
          </FormLabel>
          <Input
            value={personalInfo.github}
            onChange={(e) => onChange('github', e.target.value)}
            placeholder="github.com/johndoe"
            variant="filled"
            size="sm"
          />
        </FormControl>
      </SimpleGrid>

      <FormControl>
        <FormLabel fontSize="xs" color="whiteAlpha.600">
          {personalInfo.aboutTitle || 'About Me'}
        </FormLabel>
        <Textarea
          value={personalInfo.summary}
          onChange={(e) => onChange('summary', e.target.value)}
          placeholder="A brief professional summary highlighting your key strengths, experience, and what drives you..."
          variant="filled"
          size="sm"
          rows={10}
          maxLength={800}
        />
      </FormControl>
    </VStack>
  );
};

export default PersonalInfoForm;
